import { useEffect, useState } from 'react'

export const scriptCache: {
  loaded: boolean
  src: string
}[] = []

export type ScriptOptions = Partial<Pick<HTMLScriptElement, 'integrity' | 'crossOrigin'>>

/* If you're passing options please memoize the object */
export const useScript = (src: string, enabled = true, options: ScriptOptions = {}) => {
  const [scriptState, setScriptState] = useState<'loaded' | 'loading' | 'error' | 'disabled'>(
    enabled ? 'loading' : 'disabled'
  )

  const registerListeners = (script: HTMLScriptElement) => {
    const onScriptLoad = () => {
      const scriptIndex = scriptCache.findIndex((s) => script.src.endsWith(s.src))

      if (scriptIndex > -1) {
        scriptCache[scriptIndex] = {
          ...scriptCache[scriptIndex],
          loaded: true,
        }
      }

      setScriptState('loaded')
    }

    const onScriptError = () => {
      const scriptIndex = scriptCache.findIndex((s) => s.src === script.src)

      if (scriptIndex > -1) {
        scriptCache.splice(scriptIndex, 1)
      }

      script.remove()

      setScriptState('error')
    }

    script.addEventListener('load', onScriptLoad)
    script.addEventListener('error', onScriptError)

    return () => {
      script.removeEventListener('load', onScriptLoad)
      script.removeEventListener('error', onScriptError)
    }
  }

  useEffect(() => {
    if (!enabled) {
      setScriptState('disabled')

      return
    }

    setScriptState('loading')

    if (!src) {
      return setScriptState('error')
    }

    const scriptInCache = scriptCache.find((s) => s.src === src)

    if (scriptInCache) {
      const script = document.querySelector(`script[src='${src}']`) as HTMLScriptElement

      if (!script) {
        // this should not happen and should be dealt with as an error
        setScriptState('error')
      } else if (!scriptInCache.loaded) {
        return registerListeners(script)
      } else {
        setScriptState('loaded')
      }
    } else {
      const script = document.createElement('script')
      script.id = src
      script.src = src
      script.async = true

      for (const key of Object.keys(options)) {
        const scriptKey = key as keyof ScriptOptions
        const value = options[scriptKey]

        if (value) {
          script[scriptKey] = value
        }
      }

      scriptCache.push({ src, loaded: false })

      const removeListeners = registerListeners(script)

      document.head.insertBefore(script, document.head.firstChild)

      return removeListeners
    }
  }, [src, options, enabled])

  return scriptState
}
